import { useCallback, useEffect, useMemo, useState } from 'react';

import startCase from 'lodash/startCase';

import { useTranslate } from '@i18n';
import { useParameterGroups, useCustomModule, useSaveCustomModule, useUiParameters } from '@queries';
import { Button, Panel, Modal } from '@ui';

import { NewParameter } from '../NewParameter/NewParameter';

import { BodyPartsFilter } from './BodyPartsFilter';
import * as css from './CustomModuleEditor.css';
import { GraphParameterSelector } from './GraphParameterSelector';
import { ParameterGroups } from './ParameterGroups';
import { useTimeSeriesGraphs } from './useTimeSeriesGraphs';

interface CustomModuleEditorProps {
    customModuleId: string;
    layoutId: number;
    onClose: () => void;
    activityId: number;
    activeSwingId?: string;
}

export function CustomModuleEditor({
    customModuleId,
    onClose,
    layoutId,
    activityId,
    activeSwingId,
}: CustomModuleEditorProps) {
    const { data: customizations = [], isLoading } = useCustomModule(layoutId, customModuleId);

    const { mutateAsync: saveCustomModule } = useSaveCustomModule(layoutId, customModuleId);
    const { data: parameters = [] } = useUiParameters();
    const parametersMap = useMemo(
        () => Object.fromEntries(parameters.map((parameter) => [parameter.id, parameter] as const)),
        [parameters],
    );

    const { data: parameterGroups } = useParameterGroups();
    const { graphTypes, graphsListByType } = useTimeSeriesGraphs();

    const initialParameters = useMemo(
        () => customizations.filter((c) => c.parentUiNodeId === customModuleId).map((c) => c.childUiNodeId),
        [customModuleId, customizations],
    );

    const [selectedParameterIds, setSelectedParameterIds] = useState<string[]>(initialParameters);
    useEffect(() => {
        // sync parameters after load
        setSelectedParameterIds(initialParameters);
    }, [initialParameters]);

    const selectParameterById = useCallback(
        (parameterId: string) =>
            setSelectedParameterIds((prev) => (prev.includes(parameterId) ? prev : [...prev, parameterId])),
        [],
    );
    const removeParameterById = useCallback(
        (parameterId: string) => setSelectedParameterIds((prev) => prev.filter((id) => id !== parameterId)),
        [],
    );

    const [bodyPartsFilter, setBodyPartsFilter] = useState<string[]>([]);
    const handleFilterSelect = useCallback(
        (bodyPartId: string) =>
            setBodyPartsFilter((prev) =>
                prev.includes(bodyPartId) ? prev.filter((id) => id !== bodyPartId) : [...prev, bodyPartId],
            ),
        [],
    );

    const hasSelectedParameters = selectedParameterIds.length > 0;
    const showSwingFoundations = !bodyPartsFilter.length;

    const t = useTranslate();

    return (
        <Modal
            open
            title="Edit Custom Module"
            onDismiss={onClose}
            footer={{
                primary: (
                    <Button
                        variant="primary"
                        isDisabled={false}
                        onClick={() => saveCustomModule(selectedParameterIds).then(onClose)}
                    >
                        {t('layout_editor.custom_module.save_button')}
                    </Button>
                ),
                secondary: (
                    <Button variant="secondary" onClick={onClose}>
                        {t('layout_editor.custom_module.cancel_button')}
                    </Button>
                ),
            }}
        >
            <div className={css.root}>
                <BodyPartsFilter filter={bodyPartsFilter} onSelect={handleFilterSelect} />
                <div className={css.main}>
                    <Panel
                        title="Selected Parameters"
                        collapsible={hasSelectedParameters}
                        countChildren={!isLoading}
                        initialState={hasSelectedParameters ? 'open' : 'closed'}
                    >
                        {selectedParameterIds.map((parameterId) => {
                            const node = parametersMap[parameterId];
                            return node ? (
                                <NewParameter
                                    activityId={activityId}
                                    activeSwingId={activeSwingId}
                                    node={node}
                                    isSelected
                                    showCorridors={false}
                                    onClick={() => removeParameterById(parameterId)}
                                />
                            ) : null;
                        })}
                    </Panel>
                    {showSwingFoundations && (
                        <Panel
                            title={t('layout_editor.panel.swing_foundations')}
                            initialState="open"
                            collapsible={false}
                        >
                            {parameterGroups.map((group) => (
                                <Panel
                                    key={group.id}
                                    icon="lego"
                                    title={t(group.i18n ?? '-')}
                                    initialState="closed"
                                    collapsible
                                >
                                    <ParameterGroups
                                        groupId={group.id}
                                        selectedParameterIds={selectedParameterIds}
                                        onSelect={selectParameterById}
                                        activeSwingId={activeSwingId}
                                        activityId={activityId}
                                    />
                                </Panel>
                            ))}
                        </Panel>
                    )}
                    {graphTypes?.map((graphType) => {
                        const graphsList = graphsListByType[graphType.id];
                        if (!graphsList?.length) {
                            return null;
                        }

                        const filteredGraphsList = graphsList.filter((graph) => {
                            return (
                                !bodyPartsFilter.length ||
                                bodyPartsFilter.every((filter) => graph.categories.bodyParts.includes(filter))
                            );
                        });

                        if (!filteredGraphsList.length) {
                            return null;
                        }

                        return (
                            <Panel
                                key={graphType.id}
                                title={startCase(t(graphType.i18n ?? '-').replace('Graph', ''))}
                                initialState="open"
                                collapsible
                            >
                                {filteredGraphsList.map((graph) => (
                                    <GraphParameterSelector
                                        key={graph.id}
                                        graphId={graph.id}
                                        name={t(graph.i18n ?? '-')}
                                        onAdd={selectParameterById}
                                    />
                                ))}
                            </Panel>
                        );
                    })}
                </div>
            </div>
        </Modal>
    );
}
